import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { ColorWrapper, Separator, TextParagraph } from '@atoms'
import { Button } from '@interco/inter-ui/components/Button'
import { useFeedbackPageTracking, useGoHomeButton, useTrackingRefin } from '@hooks'
import { sendButtonClickTracking } from '@hooks/useFeedbackTracking'
import routes from '@routes/routes'
import { BridgeService } from '@services/bridge'
import { ApplicationState } from '@store/types'
import { Page } from '@templates'
import { StepRefin } from '@utils/enums'

import {
  Content,
  InstructionItem,
  InstructionList,
  StepNumber,
  TitleWrapper,
  VideoWrapper,
} from './styles'
import { TUTORIAL_STEPS, TUTORIAL_YOUTUBE_EMBED_URL } from './tutorialContent'

const GO_TO_START_LABEL = 'Ir para o início'
const GO_HOME_LABEL = 'Ir para a Home'

const isNavigationFrom = (state: unknown, route: string) =>
  typeof state === 'object' && state !== null && 'from' in state && state.from === route

const Tutorial = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isApp = useSelector((state: ApplicationState) => state.ui.navigation.isApp)
  const goHomeByBridge = useGoHomeButton('btn_voltar_home_tutorial', GO_HOME_LABEL)
  const isFromContratos = isNavigationFrom(location.state, routes.CONTRATOS)
  const buttonLabel = isFromContratos ? GO_TO_START_LABEL : GO_HOME_LABEL

  useTrackingRefin(StepRefin.TUTORIAL)
  useFeedbackPageTracking('Saiba os próximos passos', 'Saiba os próximos passos')

  const handleGoToStart = () => {
    BridgeService.tic({
      name: 'btn_ir_para_inicio_tutorial',
      action: 'ir_para_inicio',
    })
    sendButtonClickTracking(isApp, GO_TO_START_LABEL, routes.CONTRATOS)
    navigate(routes.CONTRATOS)
  }

  const handleGoHome = () => {
    if (isApp) {
      goHomeByBridge()
    } else {
      sendButtonClickTracking(isApp, GO_HOME_LABEL, routes.SUCESSO)
      navigate(routes.SUCESSO)
    }
  }

  return (
    <Page
      titleHeader="Refinanciamento"
      stickyFooter={
        <>
          <Separator margin="8px -24px" height="1px" />
          <Button
            variant="primary"
            aria-label={buttonLabel}
            onClick={isFromContratos ? handleGoToStart : handleGoHome}
          >
            {buttonLabel}
          </Button>
        </>
      }
    >
      <Content>
        <TitleWrapper>
          <ColorWrapper fontWeight="600">
            <TextParagraph variant="headline-h1" textAlign="left">
              Saiba os próximos passos
            </TextParagraph>
          </ColorWrapper>
        </TitleWrapper>

        <TextParagraph variant="body-3" textAlign="left">
          Fique por dentro de cada etapa sobre os prazos de pagamento.
        </TextParagraph>
        <VideoWrapper>
          <iframe
            src={TUTORIAL_YOUTUBE_EMBED_URL}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
          />
        </VideoWrapper>
        <InstructionList>
          {TUTORIAL_STEPS.map((item) => (
            <InstructionItem key={item.id}>
              <StepNumber>{item.id}</StepNumber>
              {item.text}
            </InstructionItem>
          ))}
        </InstructionList>
      </Content>
    </Page>
  )
}

export default Tutorial
